import {useCallback, useEffect, useRef, useState} from 'react'
import {useQuery} from '@tanstack/react-query'
import {useHistory, useLocation} from 'react-router-dom'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {
    UGC_CURSOR_URL_PARAM as CURSOR_PARAM,
    UGC_SORT_OPTIONS,
    UGC_SORT_PARAM_MAP as SORT_PARAM_MAP,
    UGC_LIMIT_NAMES
} from '../constants'
import {getUgcLimit} from '../utils/ugc-flags'

export {UGC_SORT_OPTIONS}

export const fetchUgcMediaPage = async ({placement, limit, after, sort, productId, brand, locale}) => {
    const params = new URLSearchParams()
    params.set('placement', placement)
    if (productId) params.set('productId', productId)
    if (limit) params.set('limit', String(limit))
    if (after) params.set('after', after)
    if (sort) params.set('sort', sort)
    if (brand) params.set('brand', brand)
    if (locale) params.set('locale', locale)

    const response = await fetch(`/api/bv-ugc/media?${params.toString()}`)
    if (!response.ok) {
        throw new Error(`BV UGC media request failed with status ${response.status}`)
    }
    return response.json()
}

/** Fetches one UGC media page via the BV UGC proxy. */
export const useUGCMedia = (
    {placement, productId, limit, after, brand, locale} = {},
    queryOptions = {}
) => {
    return useQuery(
        ['bv-ugc', 'media', placement, productId, limit, after, brand, locale],
        () => fetchUgcMediaPage({placement, productId, limit, after, brand, locale}),
        {
            staleTime: 5 * 60 * 1000,
            retry: false,
            ...queryOptions,
            enabled: Boolean(placement) && queryOptions.enabled !== false
        }
    )
}

const readUrlCursor = (search) => {
    const value = new URLSearchParams(search).get(CURSOR_PARAM)
    if (!value) return null
    const parts = value.split(',').map(decodeURIComponent).filter(Boolean)
    return parts.length ? parts[parts.length - 1] : null
}

const writeUrlCursor = (search, cursor) => {
    const params = new URLSearchParams(search)
    if (cursor) {
        params.set(CURSOR_PARAM, encodeURIComponent(cursor))
    } else {
        params.delete(CURSOR_PARAM)
    }
    return params.toString()
}

const getTrailStorageKey = ({placement, productId, sort, brand}) =>
    `ugcTrail:${placement}:${productId || ''}:${sort}:${brand || ''}`

const readStoredTrail = (key) => {
    try {
        if (typeof window === 'undefined' || !window.sessionStorage) return null
        const raw = window.sessionStorage.getItem(key)
        if (!raw) return null
        const parsed = JSON.parse(raw)
        return Array.isArray(parsed) ? parsed : null
    } catch (e) {
        return null
    }
}

const writeStoredTrail = (key, trail) => {
    try {
        if (typeof window === 'undefined' || !window.sessionStorage) return
        if (trail.length) {
            window.sessionStorage.setItem(key, JSON.stringify(trail))
        } else {
            window.sessionStorage.removeItem(key)
        }
    } catch (e) {
        // sessionStorage can throw in private-browsing modes or when full —
        // non-fatal, the hook just falls back to URL-only single-level nav.
    }
}

const resolveInitialTrail = ({search, placement, productId, sort, brand}) => {
    const urlCursor = readUrlCursor(search)
    if (!urlCursor) return []

    const storageKey = getTrailStorageKey({placement, productId, sort, brand})
    const storedTrail = readStoredTrail(storageKey)

    if (storedTrail?.length && storedTrail[storedTrail.length - 1] === urlCursor) {
        return storedTrail
    }

    return [urlCursor]
}

/**
 * Paginated UGC media for the landing grid, supporting forward ("Show More")
 * and backward ("Show Previous") paging.
 *
 * `fullTrail` is the complete, append-only cursor chain. `topDepth` is the
 * depth of the earliest visible page and drives "Show Previous"; `showMore`
 * never advances it because appended pages stay visible.
 *
 * @param {Object} [options]
 * @param {string} [options.placement='landing']
 * @param {string} [options.productId] - optional product filter (placement='pdp')
 * @param {string} [options.brandId] - 'dynamite' | 'garage'
 * @param {number} [options.pageSize] - explicit page size; overrides the
 *   brand+site limit resolved from `placement` (used by the expanded view's
 *   "More Looks" strip, which honours the separate expanded-view limit).
 * @param {boolean} [options.enabled=true] - when false the placement is
 *   disabled and no request is made (the hook stays idle).
 */
export const useUgcMedia = ({
    placement = 'landing',
    productId,
    brandId,
    pageSize,
    enabled = true,
    persistTrail = true
} = {}) => {
    const history = useHistory()
    const location = useLocation()
    const {app: appConfig} = getConfig()
    const {site} = useMultiSite()
    const limitName = placement === 'pdp' ? UGC_LIMIT_NAMES.PDP : UGC_LIMIT_NAMES.LANDING
    const PAGE_SIZE = pageSize || getUgcLimit(appConfig, limitName, site?.id, brandId)

    const [initialTrail] = useState(() =>
        persistTrail
            ? resolveInitialTrail({
                  search: location.search,
                  placement,
                  productId,
                  sort: UGC_SORT_OPTIONS.NEWEST,
                  brand: brandId
              })
            : []
    )

    const [sort, setSortState] = useState(UGC_SORT_OPTIONS.NEWEST)
    const [items, setItems] = useState([])
    // Complete, append-only history. NEVER sliced/shrunk.
    const [fullTrail, setFullTrail] = useState(initialTrail)
    // Depth of the earliest currently-VISIBLE page. Drives hasPrevious and
    // showPrevious's cursor calc. showMore intentionally never touches this.
    const [topDepth, setTopDepth] = useState(initialTrail.length)
    const [forwardCursor, setForwardCursor] = useState(null)
    const [resultsCount, setResultsCount] = useState(0)
    const [isLoading, setIsLoading] = useState(true)
    const [isLoadingMore, setIsLoadingMore] = useState(false)
    const [error, setError] = useState(null)
    const requestIdRef = useRef(0)
    const initialTrailRef = useRef(initialTrail)

    // Persists the RESTORE point (what a future refresh should resume at) —
    // deliberately a plain inline write, not stored as reactive state, since
    // nothing else in the hook needs to read "where a refresh would land"
    // between renders. Callers pass the depth explicitly each time.
    const saveTrail = useCallback(
        (trail, depth) => {
            if (!persistTrail) return
            const restoreSlice = trail.slice(0, depth)
            const currentCursor = restoreSlice.length ? restoreSlice[restoreSlice.length - 1] : null

            const newSearch = writeUrlCursor(location.search, currentCursor)
            history.replace(`${location.pathname}?${newSearch}`)

            const storageKey = getTrailStorageKey({placement, productId, sort, brand: brandId})
            writeStoredTrail(storageKey, restoreSlice)
        },
        [persistTrail, location.search, location.pathname, history, placement, productId, sort, brandId]
    )

    const loadAnchor = useCallback(() => {
        const requestId = ++requestIdRef.current
        setIsLoading(true)
        setError(null)

        const trail = initialTrailRef.current
        const anchorCursor = trail.length ? trail[trail.length - 1] : undefined

        fetchUgcMediaPage({
            placement,
            limit: PAGE_SIZE,
            after: anchorCursor,
            sort: SORT_PARAM_MAP[sort],
            productId,
            brand: brandId
        })
            .then((data) => {
                if (requestId !== requestIdRef.current) return
                // Refresh shows ONLY the current anchor page's items — and
                // everything before that anchor is genuinely hidden, so
                // topDepth correctly starts at the full restored trail length.
                setItems(data?.items || [])
                setResultsCount(data?.resultsCount || 0)
                setForwardCursor(data?.paging?.after || null)
                setFullTrail(trail)
                setTopDepth(trail.length)
            })
            .catch((err) => {
                if (requestId !== requestIdRef.current) return
                setError(err)
                setItems([])
                setForwardCursor(null)
                setFullTrail([])
                setTopDepth(0)
            })
            .finally(() => {
                if (requestId !== requestIdRef.current) return
                setIsLoading(false)
            })
    }, [placement, sort, productId, brandId])

    useEffect(() => {
        if (!enabled) {
            setIsLoading(false)
            return
        }
        loadAnchor()
    }, [placement, sort, productId, brandId, enabled])

    const showMore = useCallback(() => {
        if (!enabled || !forwardCursor || isLoadingMore) return

        const requestId = ++requestIdRef.current
        const usedCursor = forwardCursor
        setIsLoadingMore(true)

        fetchUgcMediaPage({
            placement,
            limit: PAGE_SIZE,
            after: usedCursor,
            sort: SORT_PARAM_MAP[sort],
            productId,
            brand: brandId
        })
            .then((data) => {
                if (requestId !== requestIdRef.current) return
                if (!data?.items?.length) return

                setItems((prev) => [...prev, ...data.items])
                setForwardCursor(data?.paging?.after || null)

                // Append at the true end of the complete trail. topDepth is
                // deliberately NOT touched here — the newly appended page is
                // immediately visible on screen (accumulated), so nothing
                // becomes newly "hidden" and Show Previous must stay exactly
                // as it was (hidden, if it was hidden a moment ago).
                setFullTrail((prevFullTrail) => {
                    const nextFullTrail = [...prevFullTrail, usedCursor]
                    // Restore point advances to this new frontier — a refresh
                    saveTrail(nextFullTrail, nextFullTrail.length)
                    return nextFullTrail
                })
            })
            .catch((err) => {
                if (requestId !== requestIdRef.current) return
                setError(err)
            })
            .finally(() => {
                if (requestId !== requestIdRef.current) return
                setIsLoadingMore(false)
            })
    }, [placement, sort, productId, brandId, forwardCursor, isLoadingMore, saveTrail, enabled])

    const showPrevious = useCallback(() => {
        if (!enabled || topDepth <= 0 || isLoadingMore) return

        const requestId = ++requestIdRef.current
        const prevCursor = topDepth - 2 >= 0 ? fullTrail[topDepth - 2] : undefined
        setIsLoadingMore(true)

        fetchUgcMediaPage({
            placement,
            limit: PAGE_SIZE,
            after: prevCursor,
            sort: SORT_PARAM_MAP[sort],
            productId,
            brand: brandId
        })
            .then((data) => {
                if (requestId !== requestIdRef.current) return
                if (!data?.items?.length) return

                setItems((prev) => [...data.items, ...prev])
                const nextTopDepth = topDepth - 1
                setTopDepth(nextTopDepth)
                saveTrail(fullTrail, nextTopDepth)
            })
            .catch((err) => {
                if (requestId !== requestIdRef.current) return
                setError(err)
            })
            .finally(() => {
                if (requestId !== requestIdRef.current) return
                setIsLoadingMore(false)
            })
    }, [placement, sort, productId, brandId, fullTrail, topDepth, isLoadingMore, saveTrail, enabled])

    const setSort = useCallback(
        (nextSort) => {
            initialTrailRef.current = []
            saveTrail([], 0)
            setSortState(nextSort)
        },
        [saveTrail]
    )

    return {
        items,
        resultsCount,
        sort,
        setSort,
        isLoading,
        isLoadingMore,
        error,
        hasMore: enabled && Boolean(forwardCursor),
        hasPrevious: topDepth > 0,
        showMore,
        showPrevious
    }
}
